"use client";

import React, { ChangeEventHandler, useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import { getColor } from "../utils";
import Icon from "../Icon";
import Flex, { FlexProps } from "../Flex";
import Text from "../Text";
import { BoxProps } from "../Box";

type Props = {
  id?: string;
  type?: "radio" | "checkbox" | "indeterminate";
  size?: "small" | "regular";
  truncate?: boolean;
  isSelected?: boolean;
  label?: string | React.ReactNode;
  onClick?: ChangeEventHandler;
  disabled?: boolean;
  styleOverrides?: BoxProps & FlexProps;
};

const CheckboxWrapper = styled(Flex).attrs({})`
  input[type="checkbox"] {
    // Hide checkbox visually but remain accessible to screen readers.
    display: none;
  }
`;

const Checkbox: React.FC<Props> = ({
  id = null,
  type = "checkbox",
  size = "regular",
  truncate = false,
  isSelected = false,
  label = null,
  onClick = null,
  disabled = false,
  styleOverrides,
}) => {
  const iconState = isSelected ? "checked" : "unchecked";
  const iconId =
    type === "indeterminate" ? "action.checkbox-indeterminate" : `${type}-${iconState}`;

  const theme = useContext(ThemeContext);
  const fill = disabled
    ? getColor("grayDark")
    : getColor((theme && theme.primaryColor) || "primary");

  let text = label;
  // Wrap string label in a Text element
  // Non string labels are typically used to include a text link in the label
  if (typeof label === "string") {
    text = (
      <Text
        font={size === "small" ? "ui.small" : "ui.regular"}
        truncate={truncate}
        color={disabled ? "grayDarker" : "black"}>
        {label}
      </Text>
    );
  }

  return (
    <CheckboxWrapper
      as="label"
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      alignItems="center"
      onClick={e => {
        e.stopPropagation();
      }}
      {...styleOverrides}>
      <input
        data-testid={id}
        type="checkbox"
        aria-checked={isSelected}
        checked={isSelected}
        onChange={disabled ? null : onClick}
        disabled={disabled}
        role={type === "radio" ? "radio" : "checkbox"}
      />
      <Icon
        disabled={disabled}
        flexShrink="0"
        fill={fill}
        iconId={iconId}
        size={size === "small" ? "16" : undefined}
        mr={text ? "xs" : null}
        aria-hidden
      />
      {text}
    </CheckboxWrapper>
  );
};

export default Checkbox;
